<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>

        <template #description>
            <div>
                欄位底色： 達 
                <span :style="{'background-color': '#FFFF00'}">大雨</span>/ 
                <span :style="{'background-color': '#FFC000'}">豪雨</span>/ 
                <span :style="{'background-color': '#FF0000'}">大豪雨</span>/ 
                <span :style="{'background-color': '#FF0000'}">超大豪雨</span> 
                標準
            </div>
        </template>

        <template #filter_condition_options>
            <MonitorOptionSet
                ref="monitor_option_set"
                :option_set="option_set"
                @filter_data="filter_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :enable_table_label="true"
                :table_label_function="calc_accumulated_precp_level"
                :show_download_doc_btn="true"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template #diagram_dialog="{ close_dialog }">
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :default_variable="$t('__rainfall.rainfall')"
                        :prop_rain_selected="current_table_header"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template #table_description>
                    <span>
                        說明："--"標示，顯示該欄位無資料。 
                    </span>
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { redirect_monitor_table_ajax } from "@/utils/monitor/monitor.js"
    import { 
        calc_accumulated_precp_level ,
        sort_float_callback_by_given_key, 
    } from "@/utils/monitor/common_tool.js"
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
    } from '@/utils/monitor/option.js'
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { TABLE_VALUE_TEXT_MAPPING } from "@/config/monitor_config.js"
    import { mapState } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"

    export default {
        name: 'BranchRainfall',

        components: { 
            MonitorLayoutTemplate,
            QpeDatetime,
            MonitorOptionSet, 
            QpeDatatable,
            GisChart: () => import('@/components/chart/gis_chart.vue')
        },
        
        mixins: [monitor_mixin],
        
        data() {
            return {
                header: [],
                table_data: [],
                default_value: 11,
                error_text: '',

                // filter使用
                immutable_table_data: [],
                intervals: ['10分鐘', '1小時', '3小時', '6小時', '12小時', '24小時', '48小時', '72小時'],
                accumulated_precps: ['顯示全部', '>=0.5mm', '>=10mm', '>=15mm', '>=30mm', '>=50mm', '>=90mm', '>=100mm'],
                option_set: [],
                display_all_office: '所有養護工程分局',
                display_all_branch: '所有工務段',

                should_draw_diagram_header: ['10分鐘', '1小時', '3小時', '6小時', '12小時', '24小時', '48小時', '72小時'],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true }
                },
                current_table_header: '',

                calc_accumulated_precp_level: calc_accumulated_precp_level,

                local_storage_option_name: 'branch_rainfall_option',
            }
        },
        
        computed: {
            ...mapState([
                'client_name'
            ]),

            table_title() {
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            }
        },
        
        watch: {
            immutable_table_data: {
                handler(){
                    if (this.immutable_table_data) {
                        this.generate_option_set()
                        this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                    }
                },
                deep: true
            },
        },

        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },
        
        methods: {
            generate_table_data (data, header) {
                this.immutable_table_data = data;
                this.table_data = this.sort_table_data(data);
                this.header = header;
            },
            
            generate_option_set () {
                const basic_variable_options = [
                    { default_value: this.display_all_office, json_key:'養護工程分局', label: 'office' },
                    { default_value: this.display_all_branch, json_key:'工務段', label: 'branch' },
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)
                
                if (this.option_set.length === 0) {
                    const fix_options = [
                        {current_option_value: '10分鐘', label: 'interval', option_array: this.intervals},
                        {current_option_value: '>=0.5mm', label: 'precps', option_array: this.accumulated_precps},
                    ]
    
                    this.option_set = [...variable_options, ...fix_options]
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data (filtered_cond, change_condition_label) {
                const update_current_value_by_filtered_cond = update_current_option_value(filtered_cond)
                this.option_set = this.option_set.map(update_current_value_by_filtered_cond)
                if (change_condition_label === 'office') {
                    this.option_set[1].current_option_value = this.display_all_branch
                    filtered_cond['branch'] = this.display_all_branch
                }

                const compare_office = filtered_cond['office'] === this.display_all_office ? '' : filtered_cond['office']
                const compare_branch = filtered_cond['branch'] === this.display_all_branch ? '' : filtered_cond['branch']
                const current_interval = filtered_cond['interval']
                let compare_interval
                const compare_accumulated_precp = filtered_cond['precps'] === '顯示全部' ? '' : filtered_cond['precps']
                
                const filtered_data = this.immutable_table_data.filter(function(item) {
                    // 原始資料 雨量 mm 顯示 -- 處理方式為改為 0 後做比較，建立新變數以避免改到原值
                    compare_interval = item[current_interval] === '--' ? 0 : item[current_interval]
                    return (
                        // 避免養護工程分局下拉選單選擇南區時，雲嘉南區的資料也一起被過濾出來
                        (item['養護工程分局'] === compare_office || (compare_office === '' && item['養護工程分局'].includes(compare_office))) &&
                        item['工務段'].includes(compare_branch) &&
                        compare_interval >= Number(compare_accumulated_precp.slice(2, -2))
                    )
                })
                this.table_data = this.sort_table_data(filtered_data)

                this.generate_branch_option(filtered_cond['office'])

                localStorage.setItem(this.local_storage_option_name, JSON.stringify(filtered_cond))
            },

            generate_branch_option (office) {
                const filter_towns_by_office = item => item['養護工程分局'] === office
                const get_branch = item => item['工務段']
                const filtered_towns_options = office === this.display_all_office ? this.immutable_table_data : this.immutable_table_data.filter(filter_towns_by_office)
                const towns_options = filtered_towns_options.map(get_branch)
                this.option_set[1].option_array = [this.display_all_branch, ...new Set(towns_options)]
            },

            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.current_table_header = header_name
                this.chart_options = { ...this.chart_options, ...get_stations_history_params.call(this, current_row_data) }
            },

            sort_table_data(data){
                const sort_by_24h_precp = sort_float_callback_by_given_key(this.$t('24小時'))
                return [...data].sort(sort_by_24h_precp)
            },
        },
    }
</script>

<style scoped>
    .description {
        padding: 5px 10px;
    }
</style>